import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CrosshairIcon, SpinnerGapIcon, XLogoIcon, TrashIcon, PlusIcon, BellRingingIcon, WarningIcon, TrophyIcon, SlidersIcon } from "@phosphor-icons/react";
import { fetchTier3, fetchKols, addKol, removeKol, saveTier3Settings } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TradeButton } from "../components/TradeButton";
import { FollowBuilderButton } from "../components/FollowBuilderButton";
import { TIER3_STYLES } from "../components/Tier3Badge";
import { useSeo } from "../lib/useSeo";
import { fmtUsd } from "../lib/format";

const T3_TUNE_FIELDS = [
  { key: "strongAt", label: "Strong at ≥" }, { key: "goodAt", label: "Good at ≥" }, { key: "earlyAt", label: "Early at ≥" },
  { key: "degenWeight", label: "Degen weight" }, { key: "convWeight", label: "Conviction weight" },
  { key: "xCap", label: "X section cap" }, { key: "kolPoints", label: "KOL points" },
];

const Tier3Tuning = ({ criteria, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const f = form || criteria || {};

  const save = async () => {
    const body = Object.fromEntries(T3_TUNE_FIELDS.map((t) => [t.key, +f[t.key]]));
    if (T3_TUNE_FIELDS.some((t) => !Number.isFinite(body[t.key]) || body[t.key] <= 0)) {
      toast.error("All tuning fields need a value above 0");
      return;
    }
    setBusy(true);
    try {
      const res = await saveTier3Settings(body);
      toast.success("Tier 3 tuned — scores recompute on the next 5-min pass");
      setForm(null); setOpen(false); onSaved(res.criteria);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save — admin login required"); }
    setBusy(false);
  };

  return (
    <div className="border border-white/10 bg-card" data-testid="tier3-tuning-panel">
      <button data-testid="tier3-tuning-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-muted-foreground hover:text-white transition-colors">
        <SlidersIcon size={15} className="text-neon" /> Tier 3 tuning (admin) — thresholds & weights, no redeploy
        <span className="ml-auto font-mono text-[10px]">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {T3_TUNE_FIELDS.map((t) => (
              <label key={t.key} className="space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground">{t.label}</span>
                <input data-testid={`tier3-tune-${t.key}`} type="number" min="0" step="any"
                  value={f[t.key] ?? ""}
                  onChange={(e) => setForm((s) => ({ ...(s || criteria), [t.key]: e.target.value }))}
                  className="w-full bg-black/40 border border-white/15 px-2.5 py-2 text-sm font-mono focus:border-neon/60 outline-none" />
              </label>
            ))}
          </div>
          <button data-testid="tier3-tune-save" onClick={save} disabled={busy || !form}
            className="px-5 py-2 bg-neon text-black text-xs font-bold hover:bg-neon/90 transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save tuning"}
          </button>
          <p className="text-[10px] text-muted-foreground font-mono">
            Strong threshold also controls when the Telegram/in-app alert fires. Good/Early auto-clamp 5+ points below the tier above.
          </p>
        </div>
      )}
    </div>
  );
};

const KolManager = () => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["kols"], queryFn: fetchKols, refetchInterval: 120000 });
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const d = q.data || {};

  const add = async () => {
    if (!handle.trim()) return;
    setBusy(true);
    try {
      await addKol(handle.trim());
      setHandle("");
      qc.invalidateQueries({ queryKey: ["kols"] });
      toast.success(`@${handle.trim().replace("@", "")} added — polled within ~15 min`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add KOL"); }
    setBusy(false);
  };

  const del = async (h) => {
    try {
      await removeKol(h);
      qc.invalidateQueries({ queryKey: ["kols"] });
      toast.success(`@${h} removed`);
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="border border-white/10 bg-card p-4 space-y-3" data-testid="kol-manager">
      <div className="flex items-center gap-2 flex-wrap">
        <XLogoIcon size={16} className="text-white" />
        <h3 className="font-heading font-bold text-sm">Tracked KOLs (admin)</h3>
        <span className="font-mono text-[10px] text-muted-foreground ml-auto">
          X reads today: {d.usage?.usedToday ?? "—"}/{d.usage?.cap ?? "—"}
        </span>
      </div>
      {d.xStatus?.needsUpgrade && (
        <p className="text-[11px] text-warn font-mono flex items-center gap-1" data-testid="kol-x-warning">
          <WarningIcon size={13} /> Your X API tier can't read timelines (401/403) — KOL polling is paused. Posting is unaffected.
        </p>
      )}
      {d.xStatus?.rateLimited && (
        <p className="text-[11px] text-warn font-mono flex items-center gap-1"><WarningIcon size={13} /> X rate limit hit — polling backs off automatically.</p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {(d.kols || []).map((k) => (
          <span key={k.handle} className={`flex items-center gap-1.5 border px-2 py-1 text-xs font-mono ${k.notFound ? "border-danger/40 text-danger line-through" : "border-white/15 text-white"}`}>
            @{k.handle}
            {k.caHits > 0 && <span className="text-neon">({k.caHits} CA{k.caHits > 1 ? "s" : ""})</span>}
            <button data-testid={`kol-remove-${k.handle}`} onClick={() => del(k.handle)} className="text-muted-foreground hover:text-danger"><TrashIcon size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="kol-add-input" value={handle} onChange={(e) => setHandle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} placeholder="@handle"
          className="flex-1 max-w-[220px] bg-black/40 border border-white/15 px-2.5 py-1.5 text-sm font-mono focus:border-neon/60 outline-none" />
        <button data-testid="kol-add-btn" onClick={add} disabled={busy}
          className="px-3 py-1.5 bg-neon text-black text-xs font-bold hover:bg-neon/90 disabled:opacity-50 flex items-center gap-1">
          <PlusIcon size={13} /> Add
        </button>
      </div>
      <p className="text-[10px] text-muted-foreground font-mono">
        2 KOLs polled every 15 min (least-recent first) for Solana CAs in their posts — a KOL posting a CA is the heaviest Tier 3 signal.
      </p>
      {(d.kols || []).some((k) => k.record?.tracked > 0) && (
        <div className="pt-2 border-t border-white/5" data-testid="kol-record">
          <div className="flex items-center gap-1.5 mb-2">
            <TrophyIcon size={14} weight="fill" className="text-[#F0C000]" />
            <span className="font-heading font-bold text-xs">KOL Hit Record (7d)</span>
            <span className="text-[10px] font-mono text-muted-foreground">— peak mcap multiple after each CA post</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[9px] font-mono uppercase text-muted-foreground border-b border-white/10">
                <th className="py-1.5 pr-3">KOL</th><th className="py-1.5 pr-3">Tracked</th>
                <th className="py-1.5 pr-3">Avg peak</th><th className="py-1.5 pr-3">Best</th><th className="py-1.5">2x+</th>
              </tr>
            </thead>
            <tbody>
              {(d.kols || []).filter((k) => k.record?.tracked > 0)
                .sort((a, b) => (b.record.avgX || 0) - (a.record.avgX || 0))
                .map((k) => (
                  <tr key={k.handle} className="border-b border-white/5" data-testid={`kol-record-${k.handle}`}>
                    <td className="py-1.5 pr-3 font-mono">@{k.handle}</td>
                    <td className="py-1.5 pr-3 font-mono">{k.record.tracked}</td>
                    <td className={`py-1.5 pr-3 font-mono font-bold ${(k.record.avgX || 0) >= 2 ? "text-neon" : "text-white"}`}>{k.record.avgX}x</td>
                    <td className="py-1.5 pr-3 font-mono text-neon">{k.record.bestX}x</td>
                    <td className="py-1.5 font-mono">{k.record.winners2x}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function Confirmation() {
  useSeo({ title: "Tier 3 Confirmation — Young Coins With Real Proof | AlphaApeing", description: "Degen score + conviction + survivor flags + KOL activity, weighted by age. Catch confirmation before it's obvious." });
  const { user } = useAuth();
  const q = useQuery({ queryKey: ["tier3"], queryFn: fetchTier3, refetchInterval: 60000 });
  const coins = q.data?.coins || [];
  const stats = q.data?.stats || {};

  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="confirmation-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><CrosshairIcon size={24} weight="fill" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">TIER 3 <span className="text-neon">CONFIRMATION</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm max-w-3xl">
          Young coins (≤48h) already showing <span className="text-white">real confirmation</span>: Degen Score and conviction building
          early (weighted harder the younger the coin), survivor/organic flags, and X/KOL activity bringing new money in.
          Strong Confirmations ({q.data?.criteria?.strongAt ?? 70}+) fire an in-app chime and a Telegram ping — toggle it on the{" "}
          <Link to="/alerts" className="text-neon hover:underline inline-flex items-center gap-1"><BellRingingIcon size={12} />Alerts page</Link>.
        </p>
        <div className="flex gap-4 mt-3 font-mono text-xs text-muted-foreground flex-wrap" data-testid="tier3-stats">
          <span>scored: <span className="text-white font-bold">{stats.scored ?? "—"}</span></span>
          <span>🎯 strong: <span className="text-neon font-bold">{stats.strong ?? "—"}</span></span>
          <span>good: <span className="text-[#F0C000] font-bold">{stats.good ?? "—"}</span></span>
          <span>KOLs tracked: <span className="text-white font-bold">{q.data?.kolsTracked ?? "—"}</span></span>
        </div>
      </div>

      {user?.role === "admin" && (
        <>
          <Tier3Tuning criteria={q.data?.criteria} onSaved={() => q.refetch()} />
          <KolManager />
        </>
      )}

      {q.isLoading ? (
        <div className="py-16 flex justify-center"><SpinnerGapIcon size={28} className="animate-spin text-neon" /></div>
      ) : coins.length === 0 ? (
        <div className="border border-white/10 bg-card py-16 text-center text-muted-foreground font-mono text-sm px-6" data-testid="tier3-empty">
          No young coins scored yet — the overlay runs every 5 minutes over the live universe. Check back shortly.
        </div>
      ) : (
        <div className="border border-white/10 bg-card overflow-x-auto" data-testid="tier3-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase text-muted-foreground border-b border-white/10">
                <th className="py-2.5 px-3">Coin</th><th className="py-2.5 px-3">Tier 3</th><th className="py-2.5 px-3">Label</th>
                <th className="py-2.5 px-3">Degen</th><th className="py-2.5 px-3">Conv</th><th className="py-2.5 px-3">KOLs</th>
                <th className="py-2.5 px-3">MCap</th><th className="py-2.5 px-3">Age</th>
                <th className="py-2.5 px-3">Why</th><th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {coins.map((c) => {
                const s = TIER3_STYLES[c.label];
                return (
                  <tr key={c.mint} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`tier3-row-${c.mint}`}>
                    <td className="py-2 px-3">
                      <Link to={`/coin/${c.mint}`} className="flex items-center gap-2 hover:text-neon">
                        {c.image && <img src={c.image} alt="" className="w-6 h-6 object-cover border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />}
                        <span className="font-bold">${(c.symbol || "?").toUpperCase()}</span>
                      </Link>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-14 h-1.5 bg-white/5"><div className={`h-full ${c.score >= 70 ? "bg-neon" : c.score >= 50 ? "bg-[#F0C000]" : "bg-white/30"}`} style={{ width: `${c.score}%` }} /></div>
                        <span className="font-mono text-xs font-bold">{c.score}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      {s ? <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${s.cls}`}>{c.label}</span>
                        : <span className="text-[10px] font-mono text-muted-foreground">{c.label}</span>}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{c.degenScore}</td>
                    <td className="py-2 px-3 font-mono text-xs">{c.conviction}</td>
                    <td className="py-2 px-3 font-mono text-xs">{c.kols > 0 ? <span className="text-neon font-bold">👑 {c.kols}</span> : "—"}</td>
                    <td className="py-2 px-3 font-mono text-xs">{fmtUsd(c.marketCap)}</td>
                    <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{c.ageHours != null ? (c.ageHours < 10 ? `${c.ageHours.toFixed(1)}h` : `${Math.round(c.ageHours)}h`) : "—"}</td>
                    <td className="py-2 px-3 text-[11px] text-muted-foreground max-w-[320px]">
                      {(c.reasons || []).slice(0, 2).join(" · ") || "—"}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5">
                        <TradeButton coin={{ address: c.mint, symbol: c.symbol, url: c.url }} size="sm" />
                        {c.creator && <FollowBuilderButton creator={c.creator} size="sm" labels={["Track dev", "Tracking"]} />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
